import { Field, Form, Formik, FormikConfig } from "formik";
import { useState, useContext } from "react";

import { CaseAdmin } from "pwapi";

import useClinicFields from "shared/hooks/useClinicFields";
import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import {
  integrationAdminSchema,
  integrationAdminWarningSchema,
} from "shared/utils/caseSchema";
import { isBillingTypeUnsupported } from "shared/utils/caseUtils";
import {
  canValidate,
  hasErrors,
  getErrors,
  getValidationWarnings,
  getValueFromObject,
} from "shared/utils/validationUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import TextInput from "components/customInputs/TextInput";
import { useAppSelector } from "store/hooks";

import FormErrors from "./FormErrors";
import FormikContext from "./FormikContext";
import { IntegrationValuesFormFields } from "./integrationFormUtils";
import { UnsupportedBillingTypeContext } from "./UnsupportedBillingTypeContext";
import {
  AdminFieldClinic,
  AdminFieldOrderingDoctor,
  AdminFieldSupervisingPhysician,
} from "../edit/fields";

type IntegrationsFormProps = {
  initialFields: IntegrationValuesFormFields;
  adminCase: CaseAdminDetail;
  onSubmit: FormikConfig<IntegrationValuesFormFields>["onSubmit"];
  setValues: Function;
  setEdited: Function;
  canUpdateCase: boolean;
};

const IntegrationsForm = ({
  initialFields,
  adminCase,
  onSubmit,
  setValues,
  setEdited,
  canUpdateCase,
}: IntegrationsFormProps) => {
  const { unsupportedBillingType, setUnsupportedBillingType } = useContext(
    UnsupportedBillingTypeContext
  );
  const canValidateForm = canValidate(
    adminCase?.patient?.clinic?.customer_type
  );
  const limitBillingTypesAdmin = useAppSelector((state) =>
    selectFeatureFlag(state, "limitBillingTypesAdmin")
  );
  const [warnings, setWarnings] = useState({});

  return (
    <Formik<IntegrationValuesFormFields>
      initialValues={{ ...initialFields, caseType: adminCase?.type }}
      enableReinitialize
      onSubmit={onSubmit}
      validate={(values: any) => {
        canValidateForm &&
          integrationAdminWarningSchema
            .validate(values, {
              abortEarly: false,
            })
            .then(() => {
              setWarnings({});
            })
            .catch((err) => {
              setWarnings(getValidationWarnings(err));
            });
      }}
      validateOnBlur={false}
      validateOnMount={true}
      validationSchema={canValidateForm ? integrationAdminSchema : null}
    >
      {({ resetForm, dirty, values, errors, touched, handleSubmit }) => {
        const allErrors =
          unsupportedBillingType && limitBillingTypesAdmin
            ? {
                ...getErrors(errors, touched),
                billing_type:
                  "The selected clinic does not support the current billing type. Please change it above.",
              }
            : getErrors(errors, touched);
        const allWarnings = getErrors(warnings, touched);
        const showErrors = hasErrors(errors);
        const emr = useClinicFields({
          clinic: values.clinic,
          ordering_doctor_id: values?.ordering_doctor?.id,
          skipMRN: true,
          flatIntegration: true,
        });
        return (
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              limitBillingTypesAdmin &&
                setUnsupportedBillingType(
                  isBillingTypeUnsupported(
                    adminCase?.billing_type,
                    values?.clinic?.billing_types
                  )
                );
              handleSubmit();
            }}
            className="AdminPanelForm"
          >
            <FormikContext setValues={setValues} setEdited={setEdited} />
            <div className="panelContainer">
              <div
                className="panelContainer__inputGroup"
                style={{ width: "100%" }}
              >
                <fieldset
                  className="panelContainer__section"
                  style={{ flex: 1 }}
                >
                  <legend className="panelContainer__section--title">
                    Patient Clinic
                  </legend>
                  <Field name="clinic">
                    {({ field, form }: any) => (
                      <AdminFieldClinic
                        {...field}
                        onChange={(newValue: any) => {
                          setTimeout(() =>
                            form.setFieldValue("clinic", newValue.object)
                          );
                          form.setFieldValue("ordering_doctor", undefined);
                          form.setFieldValue(
                            "supervising_physician",
                            undefined
                          );
                        }}
                        onClear={() => {
                          setTimeout(() =>
                            form.setFieldValue("clinic", undefined)
                          );
                          form.setFieldValue("ordering_doctor", undefined);
                          form.setFieldValue(
                            "supervising_physician",
                            undefined
                          );
                          setEdited(true);
                        }}
                        disabled={!canUpdateCase}
                        error={errors.clinic}
                        serviceOptions={{
                          case_source: adminCase.source,
                          is_archived: false,
                        }}
                      />
                    )}
                  </Field>
                </fieldset>
                <fieldset
                  className="panelContainer__section"
                  style={{ flex: 1 }}
                >
                  <legend className="panelContainer__section--title">
                    Ordering Provider
                  </legend>
                  <Field name="ordering_doctor">
                    {({ field, form }: any) => (
                      <AdminFieldOrderingDoctor
                        {...field}
                        value={field.value}
                        patientClinicId={values?.clinic?.id}
                        onChange={(newValue: any) => {
                          const ordering_doctor = newValue.object;
                          form.setFieldValue(
                            "ordering_doctor",
                            ordering_doctor
                          );
                          const ordering_doctor_id = ordering_doctor?.id;
                          const skip_provider_ids =
                            values?.clinic?.emr_mappings?.flatMap(
                              (em) => em?.settings?.skip_provider_ids || []
                            );
                          const clearIntegrationFields =
                            ordering_doctor_id &&
                            skip_provider_ids?.includes(ordering_doctor_id);
                          if (clearIntegrationFields)
                            [
                              "external_order_id",
                              "external_ezderm_order_id",
                              "external_patient_id",
                            ].forEach((f) => form.setFieldValue(f, null));

                          setEdited(true);
                        }}
                        onClear={() => {
                          form.setFieldValue("ordering_doctor", undefined);
                          setEdited(true);
                        }}
                        disabled={!canUpdateCase || !values?.clinic}
                        error={errors.ordering_doctor}
                        warning={allWarnings.ordering_doctor}
                      />
                    )}
                  </Field>
                </fieldset>
                {values?.clinic && values.clinic.use_supervising_physician && (
                  <fieldset
                    className="panelContainer__section"
                    style={{ flex: 1 }}
                    data-testid="supervising-physician"
                  >
                    <legend className="panelContainer__section--title">
                      Supervising Physician
                    </legend>
                    <Field name="supervising_physician">
                      {({ field, form }: any) => (
                        <AdminFieldSupervisingPhysician
                          {...field}
                          value={field.value}
                          patientClinicId={values?.clinic?.id}
                          onChange={(newValue: any) => {
                            form.setFieldValue(
                              "supervising_physician",
                              newValue.object
                            );
                            setEdited(true);
                          }}
                          onClear={() => {
                            form.setFieldValue(
                              "supervising_physician",
                              undefined
                            );
                            setEdited(true);
                          }}
                          disabled={!canUpdateCase}
                          error={errors.supervising_physician}
                          warning={allWarnings.supervising_physician}
                        />
                      )}
                    </Field>
                  </fieldset>
                )}
              </div>
              {adminCase.source === CaseAdmin.source.PW && (
                <fieldset className="panelContainer__section">
                  <legend className="panelContainer__section--title">
                    External IDs
                  </legend>
                  <div className="panelContainer__inputGroup">
                    {emr?.fields?.map((field) => {
                      const { name, ...rest } = field;
                      const isRequired =
                        adminCase.type !== CaseAdmin.type.CONSULT &&
                        adminCase.type !== CaseAdmin.type.MOHS_QC &&
                        rest.required;

                      return (
                        <Field name={name} key={rest.id}>
                          {({ field }: any) => (
                            <TextInput
                              {...field}
                              {...rest}
                              required={isRequired}
                              label={
                                isRequired ? `${rest.label} *` : rest.label
                              }
                              disabled={!canUpdateCase}
                              error={getValueFromObject(errors, field.name)}
                            />
                          )}
                        </Field>
                      );
                    })}
                    {(!emr || !emr?.fields) && (
                      <span>Clinic has no integrations</span>
                    )}
                  </div>
                </fieldset>
              )}
              <div className="form__actionContainer">
                {canValidateForm ? (
                  <FormErrors errors={allErrors} warnings={allWarnings} />
                ) : (
                  <ul className="form__errorList">
                    <li>
                      This is a non-diagnostic clinic. No soft data validation
                      will be performed.
                    </li>
                  </ul>
                )}
                <PathologyWatchButton
                  title="Discard changes to case"
                  color="red"
                  onClick={() => {
                    resetForm();
                  }}
                  disabled={!dirty}
                >
                  Discard Changes
                </PathologyWatchButton>
                <PathologyWatchButton
                  type="submit"
                  title="Save case"
                  disabled={!dirty || !canUpdateCase || showErrors}
                  color="green"
                >
                  Save Changes
                </PathologyWatchButton>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default IntegrationsForm;
